import { useId } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { DimensaoSatisfacao } from '../../core/_models'
import { formatarContagem, formatarNota, formatarNps } from '../../core/formatacao'
import { AjudaIndicador, montarNotasAjuda } from '../ajuda/AjudaIndicador'
import { TabelaQuebra } from './TabelaQuebra'
import { IconPaths, KTSVG } from '_metronic/helpers'

type Props = {
  /** Nome da dimensão (atendimento, Telekit ou estabelecimento), vindo do i18n. */
  titulo: string
  dimensao: DimensaoSatisfacao
  /** `rotulos.supressaoNominal` — por que a dimensão veio sem números. */
  notaSupressao?: string
  /** `rotulos.zeroNaoRespondido` — por que a nota 0 fica fora da média e do NPS. */
  notaNaoRespondido?: string
}

/**
 * Uma dimensão de satisfação. A supressão é **decidida pelo servidor e apenas
 * apresentada aqui**: quando o objeto `valores` não vem (chave nula ou ausente),
 * o card exibe o `n` e a justificativa que veio da API, e não tenta ler `media`,
 * `nps` ou `distribuicao` — número escondido sem explicação se lê como bug.
 *
 * `media`/`nps` em `null` são denominador zero ("Sem dados no período"), nunca
 * `0`; e o balde da nota 0 é "Não respondido", que fica fora da média, do NPS,
 * do `n` e das faixas de detratores/neutros/promotores.
 */
export const CardSatisfacao = ({ titulo, dimensao, notaSupressao, notaNaoRespondido }: Props) => {
  const { t } = useT()
  const idBase = useId()
  const idTitulo = `${idBase}-titulo`

  const valores = dimensao.valores
  const notasSupressao = montarNotasAjuda(idBase, [notaSupressao])

  if (!valores) {
    return (
      <div
        className='card card-flush h-100 border border-gray-300'
        role='group'
        aria-labelledby={idTitulo}
        aria-describedby={notasSupressao.length > 0 ? notasSupressao[0].id : undefined}
      >
        <div className='card-body d-flex flex-column p-6'>
          <div className='d-flex justify-content-between align-items-start gap-2 mb-3'>
            <h4 id={idTitulo} className='fs-6 fw-semibold text-gray-700 mb-0'>
              {titulo}
            </h4>
            <AjudaIndicador
              rotulo={t('indicadores.ajuda.rotulo', { titulo })}
              notas={notasSupressao}
            />
          </div>

          <p className='d-flex align-items-start text-gray-900 fs-6 mb-3'>
            <KTSVG path={IconPaths.EyeSlash} className='svg-icon-em me-2 fs-5' ariaHidden />
            <span>{t('indicadores.qualidade.satisfacao.suprimida')}</span>
          </p>

          <p className='fs-7 text-gray-900 mb-0'>
            {t('indicadores.qualidade.satisfacao.respostas_validas', {
              n: formatarContagem(dimensao.n),
            })}
          </p>
        </div>
      </div>
    )
  }

  const media = formatarNota(valores.media)
  const nps = formatarNps(valores.nps)

  const valorOuSemDados = (valor: string | null) =>
    valor === null ? (
      <dd className='d-flex align-items-center text-gray-700 fs-6 mb-0 mt-2'>
        <KTSVG path={IconPaths.CircleMinus} className='svg-icon-em me-2 fs-5' ariaHidden />
        <span>{t('indicadores.card.sem_dados')}</span>
      </dd>
    ) : (
      <dd className='fs-2hx fw-bold text-gray-900 mb-0'>{valor}</dd>
    )

  return (
    <div
      className='card card-flush h-100 border border-gray-300'
      role='group'
      aria-labelledby={idTitulo}
    >
      <div className='card-body d-flex flex-column p-6'>
        <h4 id={idTitulo} className='fs-6 fw-semibold text-gray-700 mb-3'>
          {titulo}
        </h4>

        <dl className='row mb-0'>
          <div className='col-sm-6 mb-3'>
            <dt className='fs-7 fw-normal text-gray-700'>
              {t('indicadores.qualidade.satisfacao.media')}
            </dt>
            {valorOuSemDados(media)}
          </div>
          <div className='col-sm-6 mb-3'>
            <dt className='fs-7 fw-normal text-gray-700'>
              {t('indicadores.qualidade.satisfacao.nps')}
            </dt>
            {valorOuSemDados(nps)}
          </div>
        </dl>

        <p className='fs-7 text-gray-900 mb-1'>
          {t('indicadores.qualidade.satisfacao.respostas_validas', {
            n: formatarContagem(dimensao.n),
          })}
        </p>
        <p className='fs-7 text-gray-700 mb-0'>
          {t('indicadores.qualidade.satisfacao.avaliacoes_registradas', {
            n: formatarContagem(valores.respostas),
          })}
        </p>

        <dl className='row mt-4 mb-0'>
          <div className='col-4'>
            <dt className='fs-8 fw-normal text-gray-700'>
              {t('indicadores.qualidade.satisfacao.detratores')}
            </dt>
            <dd className='fs-5 fw-semibold text-gray-900 mb-0'>
              {formatarContagem(valores.detratores)}
            </dd>
          </div>
          <div className='col-4'>
            <dt className='fs-8 fw-normal text-gray-700'>
              {t('indicadores.qualidade.satisfacao.neutros')}
            </dt>
            <dd className='fs-5 fw-semibold text-gray-900 mb-0'>
              {formatarContagem(valores.neutros)}
            </dd>
          </div>
          <div className='col-4'>
            <dt className='fs-8 fw-normal text-gray-700'>
              {t('indicadores.qualidade.satisfacao.promotores')}
            </dt>
            <dd className='fs-5 fw-semibold text-gray-900 mb-0'>
              {formatarContagem(valores.promotores)}
            </dd>
          </div>
        </dl>

        <TabelaQuebra
          titulo={t('indicadores.qualidade.distribuicao.titulo')}
          colunaCategoria={t('indicadores.qualidade.distribuicao.coluna_categoria')}
          colunaValor={t('indicadores.qualidade.distribuicao.coluna_valor')}
          dados={valores.distribuicao}
          prefixoRotulo='indicadores.qualidade.distribuicao'
          nota={notaNaoRespondido}
        />
      </div>
    </div>
  )
}
